import { modelState } from "@/utils/modelState";
import { ReservationQuote } from "../types";

export namespace ReservationQuoteStatuses {

    export const ReservationQuoteStatus = Symbol("ReservationQuoteStatus");
    export interface ReservationQuoteStatus {
        [ReservationQuoteStatus]: boolean;
    }

    export const ReservationQuoteInitialEnquiryStatus = Symbol("ReservationQuoteInitialEnquiryStatus");
    export interface ReservationQuoteInitialEnquiryStatus extends ReservationQuoteStatus {
        [ReservationQuoteInitialEnquiryStatus]: boolean;
    }

    export function isReservationQuoteInitialEnquiryStatus(val: ReservationQuoteStatus): val is ReservationQuoteInitialEnquiryStatus {
        return val && (val as ReservationQuoteInitialEnquiryStatus)[ReservationQuoteInitialEnquiryStatus] === true;
    }

    export const ReservationQuoteProposalSentStatus = Symbol("ReservationQuoteProposalSentStatus");
    export interface ReservationQuoteProposalSentStatus extends ReservationQuoteStatus {
        [ReservationQuoteProposalSentStatus]: boolean;
    }

    export function isReservationQuoteProposalSentStatus(val: ReservationQuoteStatus): val is ReservationQuoteProposalSentStatus {
        return val && (val as ReservationQuoteProposalSentStatus)[ReservationQuoteProposalSentStatus] === true;
    }

    export const ReservationQuoteProposalAcceptedStatus = Symbol("ReservationQuoteProposalAcceptedStatus");
    export interface ReservationQuoteProposalAcceptedStatus extends ReservationQuoteStatus {
        [ReservationQuoteProposalAcceptedStatus]: boolean;
    }

    export function isReservationQuoteProposalAcceptedStatus(val: ReservationQuoteStatus): val is ReservationQuoteProposalAcceptedStatus {
        return val && (val as ReservationQuoteProposalAcceptedStatus)[ReservationQuoteProposalAcceptedStatus] === true;
    }

    export const ReservationQuoteProposalNoResponseStatus = Symbol("ReservationQuoteProposalNoResponseStatus");
    export interface ReservationQuoteProposalNoResponseStatus extends ReservationQuoteStatus {
        [ReservationQuoteProposalNoResponseStatus]: boolean;
    }

    export function isReservationQuoteProposalNoResponseStatus(val: ReservationQuoteStatus): val is ReservationQuoteProposalNoResponseStatus {
        return val && (val as ReservationQuoteProposalNoResponseStatus)[ReservationQuoteProposalNoResponseStatus] === true;
    }

    export const ReservationQuoteProposalRejectedStatus = Symbol("ReservationQuoteProposalRejectedStatus");
    export interface ReservationQuoteProposalRejectedStatus extends ReservationQuoteStatus {
        [ReservationQuoteProposalRejectedStatus]: boolean;
        Reason: modelState<string | null>;
    }

    export function isReservationQuoteProposalRejectedStatus(val: ReservationQuoteStatus): val is ReservationQuoteProposalRejectedStatus {
        return val && (val as ReservationQuoteProposalRejectedStatus)[ReservationQuoteProposalRejectedStatus] === true;
    }

    export interface ReservationQuoteStatusDto {
        $type: string;
        value: ReservationQuoteStatusValueDto;
    }

    export interface ReservationQuoteStatusValueDto {
        //
    }

    export interface ReservationQuoteProposalRejectedStatusValueDto extends ReservationQuoteStatusValueDto {
        reason: string | null;
    }

    export function serializeReservationQuoteStatus(action: ReservationQuoteStatus): ReservationQuoteStatusDto {
        if (!action) {
            return null;
        }

        if (isReservationQuoteInitialEnquiryStatus(action)) {
            return {
                $type: "InitialEnquiry",
                value: {}
            };
        }

        if (isReservationQuoteProposalSentStatus(action)) {
            return {
                $type: "ProposalSent",
                value: {}
            };
        }

        if (isReservationQuoteProposalAcceptedStatus(action)) {
            return {
                $type: "ProposalAccepted",
                value: {}
            };
        }

        if (isReservationQuoteProposalNoResponseStatus(action)) {
            return {
                $type: "ProposalNoResponse",
                value: {}
            };
        }

        if (isReservationQuoteProposalRejectedStatus(action)) {
            return {
                $type: "ProposalRejected",
                value: {
                    reason: action.Reason.value
                }
            };
        }

        throw new Error("Invalid status type");
    }

    export function deserializeReservationQuoteStatus(dto: ReservationQuoteStatusDto): ReservationQuoteStatus {
        if (!dto) {
            return null;
        }

        switch (dto.$type) {
            case "InitialEnquiry":
                return createReservationQuoteInitialEnquiryStatus();
            case "ProposalSent":
                return createReservationQuoteProposalSentStatus();
            case "ProposalAccepted":
                return createReservationQuoteProposalAcceptedStatus();
            case "ProposalNoResponse":
                return createReservationQuoteProposalNoResponseStatus();
            case "ProposalRejected":
                const value = dto.value as ReservationQuoteProposalRejectedStatusValueDto;

                const status = createReservationQuoteProposalRejectedStatus();
                status.Reason.setValue(value.reason);
                status.Reason.commit();

                return status;
            default:
                throw new Error("Invalid status type");
        }
    }

    export function getReservationQuoteStatuses(): { [key: string]: string } {
        return {
            "InitialEnquiry": "Initial Enquiry",
            "ProposalSent": "Quote Sent",
            "ProposalAccepted": "Proposal Accepted, Awaiting Payment",
            "ProposalNoResponse": "No Response",
            "ProposalRejected": "Rejected"
        };
    }

    export function getReservationQuoteStatusKey(status: ReservationQuoteStatus): string {
        if (isReservationQuoteInitialEnquiryStatus(status)) {
            return "InitialEnquiry";
        }

        if (isReservationQuoteProposalSentStatus(status)) {
            return "ProposalSent";
        }

        if (isReservationQuoteProposalAcceptedStatus(status)) {
            return "ProposalAccepted";
        }

        if (isReservationQuoteProposalNoResponseStatus(status)) {
            return "ProposalNoResponse";
        }

        if (isReservationQuoteProposalRejectedStatus(status)) {
            return "ProposalRejected";
        }

        throw new Error("Invalid status type");
    }

    export function getReservationQuoteStatusName(status: ReservationQuoteStatus): string {
        if (isReservationQuoteInitialEnquiryStatus(status)) {
            return "Initial Enquiry";
        }

        if (isReservationQuoteProposalSentStatus(status)) {
            return "Quote Sent";
        }

        if (isReservationQuoteProposalAcceptedStatus(status)) {
            return "Proposal Accepted, Awaiting Payment";
        }

        if (isReservationQuoteProposalNoResponseStatus(status)) {
            return "No Response";
        }

        if (isReservationQuoteProposalRejectedStatus(status)) {
            return "Rejected";
        }

        throw new Error("Invalid status type");
    }

    export function createReservationQuoteInitialEnquiryStatus(): ReservationQuoteInitialEnquiryStatus {
        const status: ReservationQuoteInitialEnquiryStatus = {
            [ReservationQuoteStatus]: true,
            [ReservationQuoteInitialEnquiryStatus]: true
        };

        return status;
    }

    export function createReservationQuoteProposalSentStatus(): ReservationQuoteProposalSentStatus {
        const status: ReservationQuoteProposalSentStatus = {
            [ReservationQuoteStatus]: true,
            [ReservationQuoteProposalSentStatus]: true
        };

        return status;
    }

    export function createReservationQuoteProposalAcceptedStatus(): ReservationQuoteProposalAcceptedStatus {
        const status: ReservationQuoteProposalAcceptedStatus = {
            [ReservationQuoteStatus]: true,
            [ReservationQuoteProposalAcceptedStatus]: true
        };

        return status;
    }

    export function createReservationQuoteProposalNoResponseStatus(): ReservationQuoteProposalNoResponseStatus {
        const status: ReservationQuoteProposalNoResponseStatus = {
            [ReservationQuoteStatus]: true,
            [ReservationQuoteProposalNoResponseStatus]: true
        };

        return status;
    }

    export function createReservationQuoteProposalRejectedStatus(): ReservationQuoteProposalRejectedStatus {
        const status: ReservationQuoteProposalRejectedStatus = {
            [ReservationQuoteStatus]: true,
            [ReservationQuoteProposalRejectedStatus]: true,
            Reason: modelState<string | null>(null)
        };

        return status;
    }

    export function createReservationQuoteStatus(name: string): ReservationQuoteStatus {
        switch (name) {
            case "InitialEnquiry":
                return createReservationQuoteInitialEnquiryStatus();
            case "ProposalSent":
                return createReservationQuoteProposalSentStatus();
            case "ProposalAccepted":
                return createReservationQuoteProposalAcceptedStatus();
            case "ProposalNoResponse":
                return createReservationQuoteProposalNoResponseStatus();
            case "ProposalRejected":
                return createReservationQuoteProposalRejectedStatus();
            default:
                throw new Error("Invalid status name");
        }
    }
}






export function hydrateReservationQuoteStatus(reservationQuote: ReservationQuote) {

    const isDirty = () => {
        if (reservationQuote.Status.isDirty) {
            return true;
        }

        if (ReservationQuoteStatuses.isReservationQuoteProposalRejectedStatus(reservationQuote.Status.value)) {
            return isReservationQuoteProposalRejectedStatusDirty(reservationQuote.Status.value);
        }

        return false;
    }

    const signal = () => {
        reservationQuote.StatusIsDirty.setValue(isDirty());
        reservationQuote.StatusVersion.setValue(reservationQuote.StatusVersion.value + 1);
    };

    // Amount strategy
    let oldReservationStatusSubscription: () => void = null;

    function subscribeStatus() {
        if (reservationQuote.Status.value) {
            const status = reservationQuote.Status.value;

            if (ReservationQuoteStatuses.isReservationQuoteProposalRejectedStatus(status)) {
                oldReservationStatusSubscription = hydrateReservationQuoteProposalRejectedStatus(status, signal);
            } else {
                oldReservationStatusSubscription = () => { };
            }
        }
    }

    subscribeStatus();

    const unsubscribeReservationStatus = reservationQuote.Status.subscribe(() => {
        oldReservationStatusSubscription?.();

        subscribeStatus();

        signal();
    });

    return unsubscribeReservationStatus;
}

function hydrateReservationQuoteProposalRejectedStatus(status: ReservationQuoteStatuses.ReservationQuoteProposalRejectedStatus, signal: () => void) {
    return status.Reason.subscribe(() => {
        signal();
    });
}

function isReservationQuoteProposalRejectedStatusDirty(status: ReservationQuoteStatuses.ReservationQuoteProposalRejectedStatus) {
    return status.Reason.isDirty;
}
